import { useCallback, useEffect, useRef } from "react";
import { useSnackbar } from "../contexts/SnackbarContext";

const HINT_DELAY_MS = 500;
const HINT_MESSAGE = "Double click shift to focus on staff member";

/**
 * Click/double-click coordination for "focus on staff" interactions.
 * - `scheduleHint` (call on single click) shows a snackbar after a short delay,
 *   so a real double-click suppresses it.
 * - `cancelHint` (call on double click, before invoking the real action) clears
 *   the pending snackbar.
 *
 * Pairs cleanly: `onClick={scheduleHint}` / `onDoubleClick={() => { cancelHint(); doIt(); }}`.
 */
export function useFocusOnStaffClick(): { scheduleHint: () => void; cancelHint: () => void } {
	const { showSnackbar } = useSnackbar();
	const clickTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

	useEffect(() => {
		return () => {
			if (clickTimerRef.current) clearTimeout(clickTimerRef.current);
		};
	}, []);

	const scheduleHint = useCallback(() => {
		if (clickTimerRef.current) clearTimeout(clickTimerRef.current);
		clickTimerRef.current = setTimeout(() => {
			showSnackbar(HINT_MESSAGE, "info");
		}, HINT_DELAY_MS);
	}, [showSnackbar]);

	const cancelHint = useCallback(() => {
		if (clickTimerRef.current) {
			clearTimeout(clickTimerRef.current);
			clickTimerRef.current = null;
		}
	}, []);

	return { scheduleHint, cancelHint };
}
